
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// import { transparentStyles } from "components/LandingPage/StopmonitorSidebar/styles";

import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

import { Grid, Tabs, Tab, Box, Card, Icon, Switch } from "@mui/material";
import Divider from "@material-ui/core/Divider";
import { tabsClasses } from '@mui/material/Tabs';

import { useSoftUIController, setTransparentInfo, setMiniSidenav, setHiddenSidenav, setDeviceLayout } from "hooks/context";

import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

import WeatherProvider from "components/Weather";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import theme from "assets/theme";
import moment from "moment";
import SoftButton from "components/SoftComponents/SoftButton";
import StatsPieChart from "components/Charts/StatisticCards/StatsPieChart";
import UptimeDoughnutChart from "components/Charts/StatisticCards/UptimeDoughnutChart";
import { useLocation, useNavigate } from "react-router-dom";
import SoftBadgeDot from "components/SoftComponents/SoftBadgeDot";
import { customSidebarStyle, sidebarHeadersStyle, sidebarTabStyle, sidebarToggleStyle, exploreContentStyle } from "../styles/index";
import SidebarRoutes from "../NavRoutes";

import groupAPI from "services/api/groups";
import deviceAPI from "services/api/bks";
import userAPI from 'services/api/user';
import GroupLiveView from "components/Groups/GroupLiveView";
import { processDevicesData } from "utils/device";
import { processGroupData } from "utils/group";
import EventsBar from "modules/clearways/views/EventsBar";
import { setMiniNavbar } from "hooks/context";
import { sidebarWrapper } from "utils/wrapper";
import HealthStatusCard from "components/Cards/HealthStatusCard";
import StatsCounterCard from "components/Cards/StatsCounterCard";
import DeviceStatsCard from "components/Cards/DeviceStatsCard";
import LiveView from "components/LiveView";
import CameraLatest from "components/Camera/CameraLatest";
import { Close } from "@mui/icons-material";
import { getConnectivityIcon } from "utils/device";
import { getDeviceTypeIcon } from "utils/device";

function StopmonitorSidebar({ user, device, setSelectedDevice }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const [header, subheader, triheader] = device.name.split("_").map((el) => el.trim());

    return (
        <SoftBox sx={{ height: '100%', paddingBottom: '8px' }}>
            {sidebarWrapper((
                <SoftBox height="calc(100% - 16px)" p={1} sx={{ overflow: 'hidden' }}>
                    <Card sx={{ height: "100%", background: '#f9f9f9' }}>
                        <SoftBox py={2} px={2} sx={{ overflowY: 'auto' }}>
                            <SoftBox
                                mt={1}
                                mx={1}
                                display="flex"
                                flexDirection="row"
                                flexWrap="wrap"
                                alignItems="baseline"
                                sx={{ gap: 0.5 }}
                            >
                                <SoftTypography
                                    variant="h4"
                                    fontWeight="bold"
                                    sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}
                                >
                                    {header}
                                </SoftTypography>
                                <SoftTypography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    textTransform="capitalize"
                                    sx={{ color: "#777", minWidth: "fit-content" }}
                                >
                                    {subheader}
                                </SoftTypography>
                                <SoftTypography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    textTransform="capitalize"
                                    sx={{ color: "#999", minWidth: "fit-content" }}
                                >
                                    {triheader}
                                </SoftTypography>
                                {deviceLayout == 'mobile' && (
                                    <SoftBox
                                        sx={{
                                            position: 'absolute',
                                            right: '5px',
                                            top: '5px',
                                            verticalAlign: 'center',
                                            alignItems: 'center',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            backgroundColor: '#ddd',
                                            px: 0.8,
                                            py: 0.6,
                                            borderRadius: '50px'
                                        }}
                                        onClick={((e) => setSelectedDevice(null))}
                                    >
                                        <Close fontSize="small" />
                                    </SoftBox>
                                )}
                            </SoftBox>
                            <SoftBox my={1}>
                                <Divider />
                            </SoftBox>
                            <SoftBox p={3}>
                                {device.hardware_type == 'bk-camera' ? (
                                    <CameraLatest uuid={device.uuid} />
                                ) : (
                                    <SoftBox sx={{ maxHeight: '500px' }}>
                                        <LiveView device={device} isPreview={true} nexus_id={user.organisations.find((org) => org.organisation_id == device.organisation_id).nexus_id || null} deviceLayout={deviceLayout} isPerspectiveLiveview={device?.overlay_id && device.overlay_id != 'no_frame' ? true : false} />
                                    </SoftBox>
                                )}
                            </SoftBox>
                            <SoftBox my={1}>
                                <Divider />
                            </SoftBox>
                            <Card>
                                <SoftBox mx={1} my={1}>
                                    <HealthStatusCard statistics={device} variant="d5" showAdvancedDeviceLevel={showAdvancedDeviceLevel} transparent={true} />
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <Grid container>
                                        <Grid item xxs={12} x4l={6}>
                                            <StatsCounterCard
                                                count={device.battery}
                                                suffix="%"
                                                title="Battery"
                                                description=""
                                                transparent={true}
                                            />
                                        </Grid>
                                        <Box sx={{
                                            width: 0, display: "flex", justifyContent: "center",
                                            opacity: 0,
                                            [theme.breakpoints.up("x4l")]: {
                                                opacity: 1,
                                            }
                                        }}>
                                            <Divider orientation="vertical" flexItem sx={{ my: 2 }} />
                                        </Box>
                                        <Grid item xxs={12}
                                            sx={{
                                                [theme.breakpoints.up("x4l")]: {
                                                    display: 'none',
                                                }
                                            }}>
                                            <Divider orientation="horizontal" sx={{ my: 2 }} />
                                        </Grid>
                                        <Grid item xxs={12} x4l={6}>
                                            <StatsCounterCard
                                                count={device.temperature}
                                                suffix={<>&deg;C</>}
                                                title="Temperature"
                                                description=""
                                                transparent={true}
                                            />
                                        </Grid>
                                    </Grid>
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <DeviceStatsCard icon={getConnectivityIcon(device, showAdvancedDeviceLevel)} transparent={true} variant="h6" title={{ text: getConnectivityIcon(device, showAdvancedDeviceLevel).text, fontWeight: 'medium' }} />
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <DeviceStatsCard icon={getDeviceTypeIcon(device, showAdvancedDeviceLevel)} transparent={true} variant="h6" title={{ text: getDeviceTypeIcon(device, showAdvancedDeviceLevel).text, fontWeight: 'medium' }} />
                                </SoftBox>
                            </Card>
                        </SoftBox>
                    </Card>
                </SoftBox>
            ), controller, '450px', true)}
        </SoftBox>
    );
}

StopmonitorSidebar.propTypes = {
    user: PropTypes.object,
    device: PropTypes.object,
    setSelectedDevice: PropTypes.func,
}

export default StopmonitorSidebar;
